import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

const DEFAULT_LOYALTY = {
  points_per_currency: 1,
  currency_per_point: 0.05,
  min_order_to_redeem: 50,
  expiry_days: 365,
  birthday_bonus: 100,
  signup_bonus: 100,
  enabled: true,
};

export const LoyaltyPremiumSection = () => {
  const [rules, setRules] = useState(DEFAULT_LOYALTY);
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tierForm, setTierForm] = useState({ name: '', price_monthly: 0, free_shipping_threshold: 0, sort_order: 0, benefits: '', enabled: true });
  const [editingTierId, setEditingTierId] = useState(null);
  const confirm = useConfirm();

  const load = () => {
    api.get('/admin/loyalty-rules').then(({ data }) => setRules({ ...DEFAULT_LOYALTY, ...data }));
    api.get('/admin/premium-tiers').then(({ data }) => setTiers(data));
  };
  useEffect(() => { load(); }, []);

  const saveRules = async () => {
    setSaving(true);
    try {
      const payload = {
        ...rules,
        points_per_currency: Number(rules.points_per_currency),
        currency_per_point: Number(rules.currency_per_point),
        min_order_to_redeem: Number(rules.min_order_to_redeem),
        expiry_days: parseInt(rules.expiry_days, 10),
        birthday_bonus: parseInt(rules.birthday_bonus, 10),
        signup_bonus: parseInt(rules.signup_bonus, 10),
      };
      await api.put('/admin/loyalty-rules', payload);
      toast.success('Loyalty rules saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const resetTierForm = () => {
    setTierForm({ name: '', price_monthly: 0, free_shipping_threshold: 0, sort_order: 0, benefits: '', enabled: true });
    setEditingTierId(null);
  };

  const editTier = (t) => {
    setEditingTierId(t.id);
    setTierForm({
      name: t.name || '',
      price_monthly: t.price_monthly || 0,
      free_shipping_threshold: t.free_shipping_threshold || 0,
      sort_order: t.sort_order || 0,
      benefits: (t.benefits || []).join('\n'),
      enabled: !!t.enabled,
    });
  };

  const saveTier = async () => {
    if (!tierForm.name.trim()) { toast.error('Tier name is required'); return; }
    const body = {
      name: tierForm.name.trim(),
      price_monthly: Number(tierForm.price_monthly) || 0,
      free_shipping_threshold: Number(tierForm.free_shipping_threshold) || 0,
      sort_order: parseInt(tierForm.sort_order, 10) || 0,
      benefits: tierForm.benefits.split('\n').map((s) => s.trim()).filter(Boolean),
      enabled: !!tierForm.enabled,
    };
    try {
      if (editingTierId) {
        await api.patch(`/admin/premium-tiers/${editingTierId}`, { ...body, id: editingTierId });
        toast.success('Tier updated');
      } else {
        await api.post('/admin/premium-tiers', body);
        toast.success('Tier created');
      }
      resetTierForm();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteTier = async (t) => {
    const ok = await confirm({
      title: `Delete tier "${t.name}"?`,
      description: 'Any users still on this tier will lose the benefits attached to it.',
      confirmLabel: 'Delete tier',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/premium-tiers/${t.id}`);
      toast.success('Tier deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Award className="w-6 h-6 text-orange-300" /> Loyalty rules</h2>
        <p className="text-sm mt-1" style={{ color: 'var(--jw-text-muted)' }}>Points earning & redemption behaviour applied storefront-wide.</p>
      </div>
      <Card className="jw-card p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Points earned per SAR spent</label>
          <Input type="number" step="0.1" value={rules.points_per_currency} onChange={(e) => setRules({ ...rules, points_per_currency: e.target.value })} data-testid="loyalty-points-per-currency-input" />
        </div>
        <div>
          <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>SAR value per point (redemption)</label>
          <Input type="number" step="0.01" value={rules.currency_per_point} onChange={(e) => setRules({ ...rules, currency_per_point: e.target.value })} data-testid="loyalty-currency-per-point-input" />
        </div>
        <div>
          <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Min order for redemption (SAR)</label>
          <Input type="number" value={rules.min_order_to_redeem} onChange={(e) => setRules({ ...rules, min_order_to_redeem: e.target.value })} data-testid="loyalty-min-order-input" />
        </div>
        <div>
          <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Points expiry (days)</label>
          <Input type="number" value={rules.expiry_days} onChange={(e) => setRules({ ...rules, expiry_days: e.target.value })} data-testid="loyalty-expiry-input" />
        </div>
        <div>
          <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Birthday bonus (points)</label>
          <Input type="number" value={rules.birthday_bonus} onChange={(e) => setRules({ ...rules, birthday_bonus: e.target.value })} data-testid="loyalty-birthday-input" />
        </div>
        <div>
          <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Signup bonus (points)</label>
          <Input type="number" value={rules.signup_bonus} onChange={(e) => setRules({ ...rules, signup_bonus: e.target.value })} data-testid="loyalty-signup-input" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer sm:col-span-2 lg:col-span-3">
          <input type="checkbox" checked={rules.enabled} onChange={(e) => setRules({ ...rules, enabled: e.target.checked })} data-testid="loyalty-enabled-checkbox" />
          <span>Loyalty program is active</span>
        </label>
        <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
          <Button onClick={saveRules} disabled={saving} data-testid="loyalty-save-rules-button" className="jw-btn-glossy">{saving ? 'Saving...' : 'Save rules'}</Button>
        </div>
      </Card>

      <div className="pt-4">
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Crown className="w-6 h-6 text-yellow-300" /> Premium tiers</h2>
        <p className="text-sm mt-1" style={{ color: 'var(--jw-text-muted)' }}>Curate the Jwalek Premium plans customers see in their profile.</p>
      </div>

      <Card className="jw-card p-5 space-y-3" data-testid="premium-tier-form">
        <h3 className="font-semibold">{editingTierId ? 'Edit tier' : 'Add tier'}</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Name</label>
            <Input value={tierForm.name} onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })} data-testid="tier-name-input" />
          </div>
          <div>
            <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Monthly price (SAR)</label>
            <Input type="number" step="0.01" value={tierForm.price_monthly} onChange={(e) => setTierForm({ ...tierForm, price_monthly: e.target.value })} data-testid="tier-price-input" />
          </div>
          <div>
            <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Free-shipping threshold (SAR)</label>
            <Input type="number" step="0.01" value={tierForm.free_shipping_threshold} onChange={(e) => setTierForm({ ...tierForm, free_shipping_threshold: e.target.value })} data-testid="tier-shipping-input" />
          </div>
          <div>
            <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Sort order</label>
            <Input type="number" value={tierForm.sort_order} onChange={(e) => setTierForm({ ...tierForm, sort_order: e.target.value })} data-testid="tier-sort-input" />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Benefits (one per line)</label>
            <Textarea rows={4} value={tierForm.benefits} onChange={(e) => setTierForm({ ...tierForm, benefits: e.target.value })} data-testid="tier-benefits-input" placeholder={'Free shipping on every order\n10% off every order\n…'} />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={tierForm.enabled} onChange={(e) => setTierForm({ ...tierForm, enabled: e.target.checked })} data-testid="tier-enabled-checkbox" />
            <span>Show to customers</span>
          </label>
        </div>
        <div className="flex justify-end gap-2">
          {editingTierId && <Button variant="ghost" onClick={resetTierForm} data-testid="tier-cancel-button">Cancel</Button>}
          <Button onClick={saveTier} data-testid="tier-save-button" className="jw-btn-glossy">
            <Plus className="w-4 h-4 mr-1" /> {editingTierId ? 'Update tier' : 'Add tier'}
          </Button>
        </div>
      </Card>

      <Card className="jw-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Price / mo</TableHead>
              <TableHead>Free-ship threshold</TableHead>
              <TableHead>Benefits</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tiers.map((t) => (
              <TableRow key={t.id} data-testid={`admin-tier-${t.id}-row`}>
                <TableCell className="font-semibold">{t.name}</TableCell>
                <TableCell>SAR {Number(t.price_monthly).toFixed(2)}</TableCell>
                <TableCell>SAR {Number(t.free_shipping_threshold).toFixed(2)}</TableCell>
                <TableCell>
                  <div className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>
                    {(t.benefits || []).slice(0, 3).map((b, i) => <div key={i}>• {b}</div>)}
                    {(t.benefits?.length || 0) > 3 && <div>…</div>}
                  </div>
                </TableCell>
                <TableCell>
                  {t.enabled
                    ? <Badge className="bg-emerald-500/15 text-emerald-200 border-emerald-400/25">Enabled</Badge>
                    : <Badge className="bg-white/[0.06] text-white/60 border-white/10">Hidden</Badge>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => editTier(t)} data-testid={`admin-tier-${t.id}-edit-button`} title="Edit"><Edit3 className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteTier(t)} data-testid={`admin-tier-${t.id}-delete-button`} title="Delete" className="text-rose-400"><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {tiers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8" style={{ color: 'var(--jw-text-muted)' }}>No tiers configured yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export default LoyaltyPremiumSection;
